import { useState } from 'react';
import { settingsApi } from '../../api';
import { Modal, useLoader } from '../../components/common';
import { IconChevronDown, IconDollarCircle } from '../../components/icons';
import { RowMenu } from '../../components/SettingGroups';
import { SettingsPage, Panel } from './shell';

const BOOKING_GROUPS = [
  { key: 'booking', label: 'Booking' },
  { key: 'agency', label: 'Agency' },
  { key: 'walk_in', label: 'Walk-in' },
];

const PAYMENT_GROUPS = [
  { key: 'cash', label: 'เงินสด' },
  { key: 'credit_card', label: 'บัตรเครดิต' },
  { key: 'qr_promptpay', label: 'QR PromptPay' },
  { key: 'transfer', label: 'เงินโอน' },
  { key: 'e_wallet', label: 'E-Wallet' },
  { key: 'agency', label: 'เอเจนซี่' },
];

const groupLabel = (kind, key) => {
  const groups = kind === 'booking' ? BOOKING_GROUPS : PAYMENT_GROUPS;
  return groups.find((group) => group.key === key)?.label || key;
};

function HeadsetIcon({ size = 19 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 13v-1a8 8 0 0 1 16 0v1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M4 13.5c0-1.1.9-2 2-2h1v6H6a2 2 0 0 1-2-2v-2Zm16 0c0-1.1-.9-2-2-2h-1v6h1a2 2 0 0 0 2-2v-2ZM17 18c0 1.1-.9 2-2 2h-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChannelDialog({ kind, item, onClose, onSave }) {
  const editing = Boolean(item);
  const [group, setGroup] = useState(item?.group || (kind === 'booking' ? 'booking' : 'credit_card'));
  const [name, setName] = useState(item?.name || '');
  const [isActive, setIsActive] = useState(item?.isActive !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const typeLabel = kind === 'booking' ? 'ประเภทการจอง' : 'ประเภทช่องทางการรับเงิน';
  const channelLabel = kind === 'booking' ? 'ช่องทางการจอง' : `ชื่อ${groupLabel(kind, group)}*`;

  const submit = async () => {
    if (!name.trim()) {
      setError(kind === 'booking' ? 'กรุณาระบุช่องทางการจอง' : 'กรุณาระบุช่องทางการรับเงิน');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSave({ group, name: name.trim(), isActive });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={kind === 'booking' ? 'ช่องทางการจอง' : 'ช่องทางการรับเงิน'}
      onClose={onClose}
      className="channel-editor-modal"
      footer={(
        <>
          <button type="button" className="btn-outline" disabled={busy} onClick={onClose}>ยกเลิก</button>
          <button type="button" className="btn-solid" disabled={busy} onClick={submit}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      )}
    >
      {error && <div className="login-error">{error}</div>}
      <div className="channel-editor-field">
        <label>{typeLabel}</label>
        <select value={group} disabled={editing} onChange={(event) => setGroup(event.target.value)}>
          {(kind === 'booking' ? BOOKING_GROUPS : PAYMENT_GROUPS).map((option) => (
            <option key={option.key} value={option.key}>{option.label}</option>
          ))}
        </select>
      </div>
      <div className="channel-editor-subtitle">
        {kind === 'payment' ? `เพิ่ม${groupLabel(kind, group)}` : groupLabel(kind, group)}
      </div>
      <div className="channel-editor-field">
        <label>{channelLabel}</label>
        <input value={name} onChange={(event) => setName(event.target.value)} />
      </div>
      <div className="channel-editor-status">
        <span>สถานะ</span>
        <label>
          <span>ไม่ใช้งาน/ใช้งาน</span>
          <input
            type="checkbox"
            checked={isActive}
            disabled={item?.locked}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          <i className={isActive ? 'on' : ''}><b /></i>
        </label>
      </div>
    </Modal>
  );
}

function ChannelBand({ item, onEdit }) {
  return (
    <div className="booking-channel-band">
      <span>{item.name}</span>
      {!item.locked && (
        <RowMenu items={[{ label: 'แก้ไข', onClick: () => onEdit(item) }]} />
      )}
    </div>
  );
}

function BookingGroup({ title, children, agencyToggle }) {
  return (
    <section className="booking-channel-section">
      <div className="booking-channel-heading">
        <span>{title}</span>
        {agencyToggle}
      </div>
      <div className="booking-channel-grid">{children}</div>
    </section>
  );
}

function PaymentGroup({ group, items, closed, onToggle, onEdit }) {
  return (
    <section className="payment-channel-group">
      <button
        type="button"
        className="payment-channel-heading"
        onClick={onToggle}
        aria-expanded={!closed}
      >
        <span>
          <b>{group.label}</b>
          <small>({items.length} ช่องทางการรับเงิน)</small>
        </span>
        {items.length > 0 && (
          <i className={closed ? '' : 'open'}><IconChevronDown size={20} /></i>
        )}
      </button>
      {!closed && items.length > 0 && (
        <div className="payment-channel-rows">
          {items.map((item) => <ChannelBand key={item.id} item={item} onEdit={onEdit} />)}
        </div>
      )}
    </section>
  );
}

export function SettingBookingType() {
  const [tab, setTab] = useState('booking');
  const [dialog, setDialog] = useState(null);
  const [closedGroups, setClosedGroups] = useState({});
  const [forceBusy, setForceBusy] = useState(false);
  const data = useLoader(() => settingsApi.bookingChannels(), []);

  const saveDialog = async (payload) => {
    if (dialog.item) {
      if (dialog.kind === 'payment') {
        await settingsApi.updatePaymentChannel(dialog.item.id, payload);
      } else if (dialog.item.source === 'agency') {
        await settingsApi.update('agencies', dialog.item.sourceId, {
          name: payload.name,
          isActive: payload.isActive,
        });
      } else {
        await settingsApi.updateBookingChannel(dialog.item.id, payload);
      }
    } else if (dialog.kind === 'payment') {
      await settingsApi.createPaymentChannel(payload);
    } else {
      await settingsApi.createBookingChannel(payload);
    }
    setDialog(null);
    data.reload();
  };

  return (
    <SettingsPage
      tab={tab}
      onTab={setTab}
      action={tab === 'booking' ? 'สร้างช่องทางการจอง' : 'สร้างช่องทางการรับเงิน'}
      onAction={() => setDialog({ kind: tab })}
      tabs={[
        {
          key: 'booking',
          label: <span className="booking-tab-label"><HeadsetIcon /> การจอง</span>,
        },
        {
          key: 'payment',
          label: <span className="booking-tab-label"><IconDollarCircle size={19} /> ช่องทางการรับเงิน</span>,
        },
      ]}
    >
      <Panel loader={data}>
        {(loaded) => {
          const bookingItems = (loaded.bookingItems || []).map((item) => ({
            ...item,
            kind: 'booking',
          }));
          const agencyItems = (loaded.agencies || []).map((agency) => ({
            id: `agency-${agency.id}`,
            sourceId: agency.id,
            source: 'agency',
            kind: 'booking',
            group: 'agency',
            name: agency.name,
            isActive: agency.is_active !== false,
          }));
          const activeBooking = [...bookingItems, ...agencyItems].filter((item) => item.isActive);
          const disabledBooking = [...bookingItems, ...agencyItems].filter((item) => !item.isActive);
          const paymentItems = (loaded.paymentItems || []).map((item) => ({
            ...item,
            kind: 'payment',
          }));
          const activePayment = paymentItems.filter((item) => item.isActive);
          const disabledPayment = paymentItems.filter((item) => !item.isActive);
          const edit = (item) => setDialog({ kind: item.kind, item });

          if (tab === 'payment') {
            return (
              <div className="payment-channel-board">
                {PAYMENT_GROUPS.map((group) => (
                  <PaymentGroup
                    key={group.key}
                    group={group}
                    items={activePayment.filter((item) => item.group === group.key)}
                    closed={Boolean(closedGroups[group.key])}
                    onToggle={() => setClosedGroups((state) => ({
                      ...state,
                      [group.key]: !state[group.key],
                    }))}
                    onEdit={edit}
                  />
                ))}
                <PaymentGroup
                  group={{ key: 'disabled', label: 'ช่องทางการรับเงินที่ถูกปิดการใช้งาน' }}
                  items={disabledPayment}
                  closed={Boolean(closedGroups.disabled)}
                  onToggle={() => setClosedGroups((state) => ({
                    ...state,
                    disabled: !state.disabled,
                  }))}
                  onEdit={edit}
                />
              </div>
            );
          }

          return (
            <div className="booking-channel-board">
              {BOOKING_GROUPS.map((group) => (
                <BookingGroup
                  key={group.key}
                  title={group.label}
                  agencyToggle={group.key === 'agency' ? (
                    <label className="agency-code-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(loaded.forceAgencyCode)}
                        disabled={forceBusy}
                        onChange={async (event) => {
                          const checked = event.target.checked;
                          setForceBusy(true);
                          try {
                            await settingsApi.updateBookingChannelConfig({ forceAgencyCode: checked });
                            data.reload();
                          } catch (error) {
                            alert(error.message);
                          } finally {
                            setForceBusy(false);
                          }
                        }}
                      />
                      <i className={loaded.forceAgencyCode ? 'on' : ''}><b /></i>
                      <span>บังคับให้กรอกรหัสเอเจนซี่</span>
                    </label>
                  ) : null}
                >
                  {activeBooking
                    .filter((item) => item.group === group.key)
                    .map((item) => <ChannelBand key={item.id} item={item} onEdit={edit} />)}
                </BookingGroup>
              ))}

              <BookingGroup title="ช่องทางการรับเงินที่ถูกปิดการใช้งาน">
                {disabledBooking.map((item) => (
                  <ChannelBand key={`${item.source || 'config'}-${item.id}`} item={item} onEdit={edit} />
                ))}
              </BookingGroup>
            </div>
          );
        }}
      </Panel>

      {dialog && (
        <ChannelDialog
          kind={dialog.kind}
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSave={saveDialog}
        />
      )}
    </SettingsPage>
  );
}
